import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import { fetchNotification, INotiListResponse } from '@/api/NotificationApi'
import configs from '@/configs'
import { ENTRY_ROUTE_QUERY, IPageInfoResponse } from '@/domains/CommonDomain'
import {
  INotiResponse,
  INotiResponseGroupMap,
  NotificationRequestRowCount,
} from '@/domains/NotificationDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { loadingActions } from '@/modules/common/loading'
import { groupBy } from '@/utils/arrayUtils'
import { NotiResponseExposedTimeTASK } from '@/utils/bridgeUtils/features/notificationExposedTime'
import { getFormattedDate } from '@/utils/dateUtils'
import { scrollToTop } from '@/utils/domUtils'
import { redirect } from '@/utils/historyUtils'
import { increasePage } from '@/utils/reducerUtils'
import { landingRoute } from '@/utils/schemeUtils'

export const NOTIFICATION = 'Notification'

interface INotificationState {
  list: INotiResponse[]
  pageInfo: IPageInfoResponse
  latestTimestamp: number
  isError: boolean
}

const initialState: INotificationState = {
  list: [],
  pageInfo: {
    curPage: 1,
    totCnt: 30,
    totPage: 3,
  },
  latestTimestamp: 0,
  isError: false,
}

const FETCH_NOTIFICATION_LIST = `${NOTIFICATION}/fetchList`
const FETCH_LATEST_TIMESTAMP = `${NOTIFICATION}/fetchLatestTimestamp`
const INCREASE_PAGE = `${NOTIFICATION}/increasePage`
const UPDATE_NOTIFICATION = `${NOTIFICATION}/update`
const SET_LATEST_TIMESTAMP = `${NOTIFICATION}/setLatestTimeStamp`
const SET_ERROR = `${NOTIFICATION}/setError`
const REDIRECT_TO = `${NOTIFICATION}/redirectTo`

export const notificationActions = {
  fetchNotificationList: createAction(FETCH_NOTIFICATION_LIST),
  fetchLatestTimestamp: createAction(FETCH_LATEST_TIMESTAMP),
  increasePage: createAction(INCREASE_PAGE),
  updateNotification: createAction(UPDATE_NOTIFICATION),
  setLatestTimestamp: createAction(SET_LATEST_TIMESTAMP),
  setError: createAction(SET_ERROR),
  redirectTo: createAction<string>(REDIRECT_TO),
}

const reducer = {
  [UPDATE_NOTIFICATION]: (state: INotificationState, action: Action<INotiListResponse>) => {
    const { list = [], pageInfo = state.pageInfo } = action.payload

    return {
      ...state,
      pageInfo,
      list: state.list.concat(list),
    }
  },
  [INCREASE_PAGE]: increasePage,
  [SET_LATEST_TIMESTAMP]: (state: INotificationState, action: Action<any>) => {
    return { ...state, latestTimestamp: action.payload }
  },
  [SET_ERROR]: (state: INotificationState) => {
    return { ...state, isError: true }
  },
}

export const notificationSelector = {
  list: (state: IRootState): INotiResponse[] => oc(state[NOTIFICATION]).list([]),
  latestTimestamp: (state: IRootState) => state[NOTIFICATION].latestTimestamp,
  pageInfo: (state: IRootState): IPageInfoResponse => ({
    curPage: oc(state[NOTIFICATION]).pageInfo.curPage(initialState.pageInfo.curPage),
    totPage: oc(state[NOTIFICATION]).pageInfo.totPage(initialState.pageInfo.totPage),
  }),
  notificationGroupMap: (state: IRootState): INotiResponseGroupMap => {
    const list = notificationSelector.list(state)

    if (list.length === 0) {
      return {}
    }

    return groupBy(list, (item: INotiResponse) => getFormattedDate(item.trxDtm, 'YYYY.MM'))
  },
}

export function* fetchLatestTimestamp() {
  try {
    const { result } = yield call(Bridge.notificationExposedTime, {
      task: NotiResponseExposedTimeTASK.READ,
    })
    const latestTimestamp = oc(result).value(new Date().getTime())

    yield put(notificationActions.setLatestTimestamp(latestTimestamp))
  } catch (e) {
    yield put(notificationActions.setError())
    yield put(errorActions.set(e))
  }
}

export function* fetchNotificationList() {
  try {
    yield put(loadingActions.start(NOTIFICATION))

    const { curPage: page } = yield select(notificationSelector.pageInfo)
    const response = yield call(fetchNotification, {
      page,
      rowCnt: NotificationRequestRowCount,
    })

    yield put(notificationActions.updateNotification(response))

    yield call(Bridge.notificationExposedTime, {
      task: NotiResponseExposedTimeTASK.WRITE,
      value: new Date().getTime(),
    })
  } catch (e) {
    yield put(notificationActions.setError())
    yield call(scrollToTop, 0)
  } finally {
    yield put(loadingActions.finish(NOTIFICATION))
  }
}

export function* redirectTo(action: Action<string>) {
  const landingScheme = action.payload
  const { isDebitCard, isSchedulePayment, isBillPayment, route } = landingRoute(landingScheme)

  if (!landingScheme) {
    return
  }

  if (isDebitCard || isSchedulePayment || isBillPayment) {
    yield call(redirect, `${route}?${ENTRY_ROUTE_QUERY}`)

    return
  }

  yield call(Bridge.navigateTo, `${configs.APP_SCHEME}${landingScheme.substr(1)}`, false)
}

export const notificationReducer = handleActions(reducer, initialState)
export const notificationSaga = [
  takeLatest(FETCH_NOTIFICATION_LIST, fetchNotificationList),
  takeLatest(FETCH_LATEST_TIMESTAMP, fetchLatestTimestamp),
  takeLatest(REDIRECT_TO, redirectTo),
]
